import Vue from 'vue';
import _ from 'lodash';
import { assert } from '../../utils/assert';

/** Default value that should be enough for slow typers */
const defaultBounce = 1000;

/**
 * Distance-crossing event hub to listen to and emit updates
 * The regular event handlers wouldn't work if we have subcomponents changing our data
 */
export const eventHub = new Vue({ name: 'StackEventHub' });

let isUpdating = false;

/** Indicates whether or not we should care about updates */
export function setUpdateStatus(val) {
  isUpdating = val;
}


/**
 * Maps the given config to a function that auto-sets the values
 * whenever an item gets updated. To be used together with the
 * subscribe method.
 *
 * @param array
 * @param mapping
 */
export function mapUpdate(array, mapping) {
  assert(_.isObject(mapping), 'Mappings need to be an object containing local -> foreign mappings');

  return (data, id, key, value) => {
    const mappedKey = mapping[key];
    if (!mappedKey) return;

    const item = _.find(array, ['id', id]);
    if (item) {
      Vue.set(item, mappedKey, value);
    }
  };
}

/**
 * Notifies the view stack of an update.
 * To be used when not using reactive properties (e.g. replaceing objects, adding new ones).
 *
 * @param that The instance we're calling from, used to figure out its uuid in the stack
 * @param key The key on the data object
 * @param value The new value we want to save
 * @param block Used to block the watchers when we do mass updates
 */
export function update(that, key, value, block = true) {
  eventHub.$emit('update', {
    uuid: that.getUuidInItemStack(), key, value, block,
  });
}

/**
 * Uses lodash's debounce function with a global default value.
 *
 * @param func
 * @return {Function|*}
 */
export function debounce(func, bounce = defaultBounce) {
  // The debounce watchers break the system if we simply fill in a new object
  // As their code gets executed later, vue has ticked through the DOM many times already
  const bounced = _.debounce(func, bounce);

  function debounced(val, old) {
    if (!isUpdating) {
      bounced.apply(this, [val, old]);
    }
  }

  // Expose lodash debounce methods
  debounced.cancel = bounced.cancel;
  debounced.flush = bounced.flush;

  return debounced;
}

/**
 * Creates a watcher that automatically sends a PUT request to the given URL
 * (which needs to be a function). The data sent is a simple key-value mapping.
 *
 * While sending the request it creates a notification which will be auto-closed on success
 * or kep open when an error occurs (and tries to get a human readable error message).
 *
 * @param url
 * @param key
 * @param display
 * @return {Function}
 */
export function watch(url, key, display, success, transformer = undefined) {
  return function watchThenNotify(val, old) {
    if (isUpdating) return;

    const value = transformer !== undefined ? transformer(val) : val;
    const previous = transformer !== undefined ? transformer(old) : old;
    this.$notifyAsync(url, key, value, previous, display, success);
  };
}

/**
 * Only calls the second function if the first returns a truthy value.
 *
 * @param first
 * @param then
 * @return {Function}
 */
export function passes(first, then) {
  return function checkThenContinue(...args) {
    if (first.apply(this, args)) {
      then.apply(this, args);
    }
  };
}

/**
 * Maps the given values to a computed getter/setter pair that will
 * emit an event on change to update the parent stack and its store.
 *
 * @param prop
 * @param values
 */
export function mapReactive(prop, values) {
  const isArray = _.isArray(values);

  assert(_.isObject(values) || isArray, `Parameter is neither object nor array: ${values}`);

  if (isArray) {
    const zipped = _.zipObject(values, values);
    values = zipped;
  }

  const dot = prop.indexOf('.');
  const prefix = dot >= 0 ? `${prop.substring(dot + 1)}.` : '';

  return _.mapValues(values, (value, key) => {
    const obj = _.isString(value) ? { key: value } : _.assign({ key }, value);

    // No way to shorten this with ES6 cause scopes
    return {
      set(val) {
        update(this, prefix + obj.key, obj.processor ? obj.processor(val) : val, false);
      },

      get() {
        return _.get(this, `${prop}.${obj.key}`);
      },
    };
  });
}



// WEBPACK FOOTER //
// ./resources/assets/js/view-stack/utils/reactive.js